import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Anchor,
    Badge,
    Button,
    Card,
    Center,
    Group,
    Loader,
    Modal,
    Pagination,
    Select,
    Stack,
    Switch,
    Table,
    Text,
    Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconRefresh, IconSend, IconSearch } from '@tabler/icons-react';
import { setCandidateMonitored } from '../api/candidates';
import {
    getMatches,
    publish,
    resetCandidateMatches,
    runCandidateFullMatch,
    runCandidateMatch,
} from '../api/monitoring';
import type { CveSortField, SortOrder, VendorCveMatch } from '../api/types';
import { SeverityBadge } from './SeverityBadge';
import { SortableTh } from './SortableTh';

const DEFAULT_PAGE_SIZE = 25;

// Filter option sets. Severity values are sent uppercase to match the stored
// `cvss_severity` column (the API also uppercases). `match_signal` and `status`
// mirror the values the matching engine writes (see vendor-global-service).
const SEVERITY_OPTIONS = [
    { value: 'CRITICAL', label: 'Critical' },
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
];

const SIGNAL_OPTIONS = [
    { value: 'cpe', label: 'CPE' },
    { value: 'kev', label: 'KEV' },
];

const STATUS_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'published', label: 'Published' },
    { value: 'failed', label: 'Failed' },
];

interface CveMonitoringCardProps {
    // Numeric candidate id — keys the matches read / cache AND the Find CVEs /
    // Reset mutations (VI-473). Unique per vendor, unlike the compound id which
    // collides on `::default`.
    catalogProductCandidateId: number;
    // VI-556 — current explicit-monitoring state, driving the "Monitored" switch.
    isMonitored: boolean;
    // Whether monitoring can be enabled at all: matching produces nothing without
    // CPE mappings, so the switch is disabled until at least one exists.
    canMonitor: boolean;
}

/**
 * CVE Monitoring panel.
 *
 * Renders the per-vendor matches table (filterable + sortable) and the control
 * buttons (Find CVEs / Publish / Reset), plus the VI-556 "Monitored" toggle.
 * Mutation feedback flows through `@mantine/notifications` toasts.
 */
export const CveMonitoringCard = ({
    catalogProductCandidateId,
    isMonitored,
    canMonitor,
}: CveMonitoringCardProps): React.JSX.Element => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
    const [severity, setSeverity] = useState<string | null>(null);
    const [matchSignal, setMatchSignal] = useState<string | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [kevOnly, setKevOnly] = useState(false);
    const [sortField, setSortField] = useState<CveSortField | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const offset = (page - 1) * DEFAULT_PAGE_SIZE;

    const { data, isLoading, refetch } = useQuery({
        queryKey: [
            'cve-matches',
            catalogProductCandidateId,
            page,
            severity,
            matchSignal,
            status,
            kevOnly,
            sortField,
            sortOrder,
        ],
        queryFn: () =>
            getMatches({
                catalog_product_candidate_id: catalogProductCandidateId,
                include_cve_details: true,
                cvss_severity: severity ?? undefined,
                match_signal: matchSignal ?? undefined,
                status: status ?? undefined,
                is_kev: kevOnly ? 'true' : undefined,
                sort: sortField ?? undefined,
                order: sortField ? sortOrder : undefined,
                limit: DEFAULT_PAGE_SIZE,
                offset,
            }),
    });

    const matches = data?.data ?? [];
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));

    const hasActiveFilters =
        severity !== null ||
        matchSignal !== null ||
        status !== null ||
        kevOnly ||
        sortField !== null;

    // Filters render above the table so they stay reachable even when the active
    // combination returns nothing. A never-matched card (no data, no filters)
    // keeps its clean empty state — filters appear once matches exist or a
    // filter/sort is applied.
    const showFilters = matches.length > 0 || hasActiveFilters;

    // Toggle direction when re-clicking the active column; otherwise activate the
    // column ascending. Mirrors the list views' SortableTh wiring (VI-511).
    const handleSort = (field: CveSortField): void => {
        if (sortField === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setPage(1);
    };

    const clearFilters = (): void => {
        setSeverity(null);
        setMatchSignal(null);
        setStatus(null);
        setKevOnly(false);
        setSortField(null);
        setSortOrder('asc');
        setPage(1);
    };

    const invalidateVendorList = (): Promise<void> =>
        queryClient.invalidateQueries({ queryKey: ['vendors'] });

    const findMutation = useMutation({
        mutationFn: () => runCandidateMatch(catalogProductCandidateId),
        onSuccess: async (result) => {
            setPage(1);
            await refetch();
            await invalidateVendorList();
            notifications.show({
                color: 'green',
                title: 'Matching complete',
                message: `Found ${result.matchesFound.toString()} matches (${result.newMatches.toString()} new, ${result.existingMatches.toString()} existing).`,
            });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to run CVE matching',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const publishMutation = useMutation({
        mutationFn: () => publish(),
        onSuccess: async () => {
            await refetch();
            notifications.show({
                color: 'green',
                message: 'Matches published to SQS.',
            });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to publish matches',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const resetMutation = useMutation({
        mutationFn: () => resetCandidateMatches(catalogProductCandidateId),
        onSuccess: async (result) => {
            setPage(1);
            await refetch();
            await invalidateVendorList();
            const deleted = result.deletedCount ?? result.deletedMatches ?? 0;
            notifications.show({
                color: 'green',
                title: 'Reset complete',
                message: `Deleted ${deleted.toString()} matches.`,
            });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to reset matches',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    // Historical backfill on a 0→1 monitoring flip, kept as its OWN mutation so a
    // backfill failure can never mask a monitoring toggle that actually succeeded
    // (see monitoredMutation). [unblocked review]
    const backfillMutation = useMutation({
        mutationFn: () => runCandidateFullMatch(catalogProductCandidateId),
        onSuccess: async (result) => {
            setPage(1);
            await refetch();
            notifications.show({
                color: 'green',
                title: 'Backfill complete',
                message: `Matched ${result.matchesFound.toString()} pre-existing CVEs for this vendor.`,
            });
        },
        onError: (err) => {
            // Monitoring is already ON and the Switch reflects it — surface the
            // backfill failure as a non-blocking warning with a retry path, NOT as
            // a "failed to update monitoring" error.
            notifications.show({
                color: 'yellow',
                title: 'Monitoring enabled — backfill pending',
                message: `The one-time historical re-match didn't finish (${err instanceof Error ? err.message : 'unknown error'}). Toggle Monitored off and back on to retry.`,
            });
        },
    });

    const monitoredMutation = useMutation({
        // Flag flip ONLY. The historical backfill is deliberately NOT awaited here:
        // bundling it would roll a backfill failure into onError, masking a toggle
        // that actually succeeded and leaving the Switch bound to a stale prop.
        // The backfill runs as a separate mutation from onSuccess. [unblocked review]
        mutationFn: (next: boolean) => setCandidateMonitored(catalogProductCandidateId, next),
        onSuccess: async (res) => {
            setPage(1);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['vendor', catalogProductCandidateId] }),
                invalidateVendorList(),
                refetch(),
            ]);
            notifications.show({
                color: 'green',
                title: res.is_monitored ? 'Monitoring enabled' : 'Monitoring disabled',
                message: res.is_monitored
                    ? 'This vendor is now monitored.'
                    : 'This vendor is no longer monitored.',
            });
            // On a genuine 0→1 flip, kick the historical backfill separately so its
            // outcome is independent of this (already-successful) toggle.
            if (res.transitioned_on) {
                backfillMutation.mutate();
            }
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to update monitoring',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const isBusy =
        findMutation.isPending ||
        publishMutation.isPending ||
        resetMutation.isPending ||
        monitoredMutation.isPending ||
        backfillMutation.isPending;

    const handleMonitoredChange = (checked: boolean): void => {
        // Enabling is the expensive path (full re-match + tenant fan-out) — confirm
        // first. Disabling is cheap and immediate.
        if (checked) {
            openConfirm();
        } else {
            monitoredMutation.mutate(false);
        }
    };

    return (
        <>
            <Card withBorder radius="md" padding="lg" data-testid="CveMonitoringCard">
                <Group justify="space-between" mb="md">
                    <Group gap="sm">
                        <Title order={4}>CVE Monitoring</Title>
                        <Switch
                            checked={isMonitored}
                            onChange={(e) => handleMonitoredChange(e.currentTarget.checked)}
                            disabled={
                                !canMonitor ||
                                monitoredMutation.isPending ||
                                backfillMutation.isPending
                            }
                            label="Monitored"
                            labelPosition="left"
                            size="sm"
                            data-testid="MonitoredToggle"
                        />
                        {!canMonitor && (
                            <Text size="xs" c="dimmed">
                                Add CPE mappings to enable
                            </Text>
                        )}
                    </Group>
                    <Group gap="xs">
                        <Button
                            size="xs"
                            leftSection={<IconSearch size={14} />}
                            onClick={() => findMutation.mutate()}
                            loading={findMutation.isPending}
                            disabled={isBusy && !findMutation.isPending}
                            data-testid="FindCvesButton"
                        >
                            Find CVEs
                        </Button>
                        <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconSend size={14} />}
                            onClick={() => publishMutation.mutate()}
                            loading={publishMutation.isPending}
                            disabled={isBusy && !publishMutation.isPending}
                            data-testid="PublishButton"
                        >
                            Publish
                        </Button>
                        <Button
                            size="xs"
                            variant="subtle"
                            color="red"
                            leftSection={<IconRefresh size={14} />}
                            onClick={() => resetMutation.mutate()}
                            loading={resetMutation.isPending}
                            disabled={isBusy && !resetMutation.isPending}
                            data-testid="ResetButton"
                        >
                            Reset
                        </Button>
                    </Group>
                </Group>

                {showFilters ? (
                    <Group gap="sm" mb="md" align="center" data-testid="CveMonitoringFilters">
                        <Select
                            aria-label="Filter by severity"
                            placeholder="Severity"
                            data={SEVERITY_OPTIONS}
                            value={severity}
                            onChange={(value) => {
                                setSeverity(value);
                                setPage(1);
                            }}
                            clearable
                            size="xs"
                            w={130}
                            data-testid="CveSeverityFilter"
                        />
                        <Select
                            aria-label="Filter by match signal"
                            placeholder="Signal"
                            data={SIGNAL_OPTIONS}
                            value={matchSignal}
                            onChange={(value) => {
                                setMatchSignal(value);
                                setPage(1);
                            }}
                            clearable
                            size="xs"
                            w={120}
                            data-testid="CveSignalFilter"
                        />
                        <Select
                            aria-label="Filter by status"
                            placeholder="Status"
                            data={STATUS_OPTIONS}
                            value={status}
                            onChange={(value) => {
                                setStatus(value);
                                setPage(1);
                            }}
                            clearable
                            size="xs"
                            w={130}
                            data-testid="CveStatusFilter"
                        />
                        <Switch
                            label="KEV only"
                            checked={kevOnly}
                            onChange={(event) => {
                                setKevOnly(event.currentTarget.checked);
                                setPage(1);
                            }}
                            size="sm"
                            data-testid="CveKevFilter"
                        />
                        {hasActiveFilters ? (
                            <Button
                                variant="subtle"
                                color="gray"
                                size="xs"
                                onClick={clearFilters}
                                data-testid="CveClearFiltersButton"
                            >
                                Clear filters
                            </Button>
                        ) : null}
                    </Group>
                ) : null}

                {isLoading ? (
                    <Center py="xl">
                        <Loader />
                    </Center>
                ) : matches.length === 0 ? (
                    <Center py="xl">
                        <Text c="dimmed" data-testid="CveMatchesEmpty">
                            {hasActiveFilters
                                ? 'No CVE matches for the selected filters.'
                                : 'No CVE matches for this vendor.'}
                        </Text>
                    </Center>
                ) : (
                    <Stack gap="md">
                        <Table.ScrollContainer minWidth={800}>
                            <Table highlightOnHover striped data-testid="CveMatchesTable">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>CVE ID</Table.Th>
                                        <Table.Th>Description</Table.Th>
                                        <Table.Th w={70}>Signal</Table.Th>
                                        <SortableTh<CveSortField>
                                            label="Severity"
                                            field="cvss_severity"
                                            activeField={sortField}
                                            order={sortOrder}
                                            onSort={handleSort}
                                            width={100}
                                        />
                                        <SortableTh<CveSortField>
                                            label="CVSS"
                                            field="cvss_score"
                                            activeField={sortField}
                                            order={sortOrder}
                                            onSort={handleSort}
                                            width={90}
                                        />
                                        <Table.Th w={70}>KEV</Table.Th>
                                        <Table.Th w={80}>EPSS</Table.Th>
                                        <SortableTh<CveSortField>
                                            label="Found"
                                            field="created_at"
                                            activeField={sortField}
                                            order={sortOrder}
                                            onSort={handleSort}
                                            width={120}
                                        />
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {matches.map((m) => (
                                        <CveRow key={m.id} match={m} />
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>

                        <Group justify="space-between" align="center">
                            <Text size="sm" c="dimmed">
                                Showing {offset + 1}–{Math.min(offset + matches.length, total)} of{' '}
                                {total}
                            </Text>
                            <Pagination value={page} onChange={setPage} total={pageCount} />
                        </Group>
                    </Stack>
                )}
            </Card>
            <Modal
                opened={confirmOpen}
                onClose={closeConfirm}
                title="Enable continuous monitoring?"
                centered
                data-testid="EnableMonitoringModal"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Enabling monitoring runs a full CVE re-match for this vendor and can surface
                        many historical CVEs. Monitoring is the expensive path — keep it to the few
                        vendors that need continuous coverage.
                    </Text>
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={closeConfirm}
                            data-testid="CancelEnableMonitoring"
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={monitoredMutation.isPending}
                            onClick={() => {
                                closeConfirm();
                                monitoredMutation.mutate(true);
                            }}
                            data-testid="ConfirmEnableMonitoring"
                        >
                            Enable monitoring
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

interface CveRowProps {
    match: VendorCveMatch;
}

const CveRow = ({ match }: CveRowProps): React.JSX.Element => {
    return (
        <Table.Tr data-testid="CveMatchRow">
            <Table.Td>
                <Anchor
                    href={`https://nvd.nist.gov/vuln/detail/${match.cve_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                >
                    {match.cve_id}
                </Anchor>
            </Table.Td>
            <Table.Td>
                <Text size="sm" lineClamp={2}>
                    {match.cve_description ?? '—'}
                </Text>
            </Table.Td>
            <Table.Td>
                <Badge size="sm" variant="default">
                    {match.match_signal.toUpperCase()}
                </Badge>
            </Table.Td>
            <Table.Td>
                {match.cvss_severity ? (
                    <SeverityBadge severity={match.cvss_severity} />
                ) : (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                )}
            </Table.Td>
            <Table.Td>
                <Text size="sm">{match.cvss_score?.toString() ?? '—'}</Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm">{match.is_kev ? 'Yes' : 'No'}</Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm">{match.epss_score?.toFixed(4) ?? '—'}</Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">
                    {new Date(match.created_at).toLocaleDateString()}
                </Text>
            </Table.Td>
        </Table.Tr>
    );
};
